import { useEffect, useRef, useState, ComponentType, Suspense, lazy } from "react";

/**
 * Hook that lazy-loads a component when the wrapper enters the viewport.
 * Returns a wrapper <div> that triggers loading at `rootMargin` distance.
 */
export function useLazySection<P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>,
  options?: { rootMargin?: string }
) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [LazyComponent, setLazyComponent] = useState<React.LazyExoticComponent<ComponentType<P>> | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLazyComponent(lazy(factory));
          observer.disconnect();
        }
      },
      {
        rootMargin: options?.rootMargin ?? "200px 0px",
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { sentinelRef, Component: LazyComponent };
}

interface LazySectionProps<P extends object> {
  factory: () => Promise<{ default: ComponentType<P> }>;
  props: P;
  rootMargin?: string;
  fallback?: React.ReactNode;
  className?: string;
}

export function LazySection<P extends object>({
  factory,
  props,
  rootMargin,
  fallback,
  className,
}: LazySectionProps<P>) {
  const { sentinelRef, Component } = useLazySection<P>(factory, { rootMargin });

  return (
    <div ref={sentinelRef} className={className}>
      {Component ? (
        <Suspense fallback={fallback ?? null}>
          <Component {...(props as any)} />
        </Suspense>
      ) : (
        fallback ?? <div className="min-h-[200px]" />
      )}
    </div>
  );
}
